import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useRooms } from "@/data/rooms";
import { MaskLines, Reveal } from "@/components/MaskLines";
import { useLang } from "@/lib/i18n";

export const Marquee = () => {
  const words = useRooms().map((r) => r.name);
  const row = [...words, ...words];
  return (
    <section className="overflow-hidden border-y border-white/10 py-8" data-testid="marquee">
      <div className="marquee-track">
        {row.map((w, i) => (
          <span key={i} className="flex items-center gap-10 pr-10 font-display text-5xl italic md:text-7xl">
            <span className={i % 2 ? "outline-text" : ""}>{w}</span>
            <span className="h-3 w-3 rotate-45 bg-[var(--brick)]" />
          </span>
        ))}
      </div>
    </section>
  );
};

const STATS = [["30 m", "stats.length"], ["6 m", "stats.width"], ["6", "stats.rooms"], ["1.5 m", "stats.arch"]];

export const Building = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  return (
    <section ref={ref} className="mx-auto grid max-w-[1440px] gap-14 px-5 py-28 md:px-10 lg:grid-cols-12 lg:py-40" data-testid="building-section">
      <div className="lg:col-span-6">
        <p className="mono text-xs text-[var(--brick-light)]">{t("bld.kicker")}</p>
        <MaskLines inView className="mt-4 font-display text-5xl leading-[0.95] lg:text-7xl" lines={[t("bld.l1"), <em key="a">{t("bld.l2")}</em>]} />
        <Reveal delay={0.2}>
          <p className="mt-8 max-w-md text-base leading-relaxed text-[var(--muted)] md:text-lg">{t("bld.body")}</p>
        </Reveal>
        <div className="mt-14 grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-white/10 bg-white/10">
          {STATS.map(([n, l], i) => (
            <Reveal key={l} delay={i * 0.08} className="bg-[var(--bg)] p-6">
              <p className="font-display text-5xl" data-testid={`stat-${i}`}>{n}</p>
              <p className="mono mt-2 text-[11px] text-[var(--muted)]">{t(l)}</p>
            </Reveal>
          ))}
        </div>
      </div>
      <Reveal className="lg:col-span-5 lg:col-start-8">
        <div className="arch-frame relative aspect-[6/7] bg-[#e8e4de]">
          <motion.img style={{ y }} src="/img/plan.webp" alt={t("bld.alt")} className="absolute inset-0 h-[116%] w-full -translate-y-[8%] object-cover" />
        </div>
        <p className="mono mt-4 text-[11px] text-[var(--muted)]">{t("bld.caption")}</p>
      </Reveal>
    </section>
  );
};

const SPANS = [
  "lg:col-span-6 lg:row-span-2",
  "lg:col-span-3 lg:row-span-2",
  "lg:col-span-3",
  "lg:col-span-3",
  "lg:col-span-5",
  "lg:col-span-7",
];

export const RoomsBento = () => {
  const { t } = useLang();
  const rooms = useRooms();
  return (
  <section className="mx-auto max-w-[1440px] px-5 py-24 md:px-10" data-testid="rooms-bento">
    <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
      <div>
        <p className="mono text-xs text-[var(--brick-light)]">{t("bento.kicker")}</p>
        <MaskLines inView className="mt-4 font-display text-5xl leading-[0.95] lg:text-7xl" lines={[t("bento.l1"), <em key="b">{t("bento.l2")}</em>]} />
      </div>
      <Link to="/salas" className="btn-ghost self-start md:self-auto" data-testid="bento-all-rooms">{t("bento.all")} <ArrowUpRight size={16} /></Link>
    </div>
    <div className="mt-14 grid auto-rows-[260px] grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-12 lg:auto-rows-[240px]">
      {rooms.map((r, i) => (
        <Reveal key={r.id} delay={(i % 3) * 0.08} className={`${SPANS[i]} h-full`}>
          <Link to={`/salas/${r.id}`} className="group relative block h-full overflow-hidden rounded-2xl border border-white/10" data-testid={`bento-${r.id}`}>
            <img src={r.img} alt={r.name} className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-110" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 flex items-end justify-between p-5">
              <div>
                <p className="mono text-[11px]" style={{ color: r.color }}>{r.num} · {r.dims}</p>
                <p className="mt-1 font-display text-3xl lg:text-4xl">{r.name}</p>
              </div>
              <span className="grid h-10 w-10 place-items-center rounded-full border border-white/30 transition-all duration-500 group-hover:rotate-45 group-hover:bg-[var(--ink)] group-hover:text-[var(--bg)]">
                <ArrowUpRight size={16} />
              </span>
            </div>
          </Link>
        </Reveal>
      ))}
    </div>
  </section>
  );
};
